/**
 * Layout constants for the organisation dashboard (30-column grid).
 */
export const ORG_DASHBOARD_LAYOUT_VERSION = 15;

// Master switch for the org-view left-panel org→site navigation. Ships on by
// default; set REACT_APP_ORG_SITE_NAV_ENABLED=false at build time to hide the
// panel without a code change (site dashboard is unaffected).
export const ORG_SITE_NAV_ENABLED = process.env.REACT_APP_ORG_SITE_NAV_ENABLED !== 'false';

export const ORG_DASHBOARD_COLS = 30;
const ORG_MID_WIDTH = ORG_DASHBOARD_COLS / 3;
const ORG_GRAPHICS_WIDGET = { chart_type: 'graphics' };

/** Site widgets also allowed on org dashboards — force org-grid sizes (30-col). */
const ORG_SHARED_SITE_WIDGET = {
  min_width: ORG_MID_WIDTH,
  min_height: 4,
  widget_metadata: {
    recommended_width: ORG_MID_WIDTH,
    recommended_height: 4
  }
};

/** Count-style shared widgets: BigNumber's 60/16 defaults are sized for a narrow
 * site tile and dwarf a wide org one, so match the org KPI sizing. */
const ORG_SHARED_COUNT_WIDGET = {
  ...ORG_SHARED_SITE_WIDGET,
  numberSize: 32,
  labelSize: 11
};

/** Layout-only overrides merged onto API widget metadata. */
export const ORG_LOCAL_WIDGET_METADATA = {
  org_sites_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_total_defects_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_unread_unanalysed_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_asset_instrumentation_tasks_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_fleet_health_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_device_reachability_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_cost_saved_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_site_scorecard: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_DASHBOARD_COLS, min_height: 3 },
  org_open_events_by_site: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  org_device_health_rollup: { ...ORG_GRAPHICS_WIDGET, min_width: ORG_MID_WIDTH, min_height: 4 },
  // Dual-tagged site widgets (org builders exist) — avoid 1–2 col tiles on 30-col grid
  active_defects_detected: { ...ORG_SHARED_SITE_WIDGET },
  plant_asset_health: { ...ORG_SHARED_SITE_WIDGET },
  device_reachability_breakdown: { ...ORG_SHARED_SITE_WIDGET },
  open_closed_tasks: { ...ORG_SHARED_COUNT_WIDGET },
  total_cost_saved: { ...ORG_SHARED_COUNT_WIDGET }
};

export const ORG_DEFAULT_WIDGET_KEYS = Object.keys(ORG_LOCAL_WIDGET_METADATA);

/** Client-side fallback when API template is unavailable. */
export const DEFAULT_ORG_DASHBOARD = {
  id: 'org-dashboard',
  name: 'Organisation Dashboard',
  layoutVersion: ORG_DASHBOARD_LAYOUT_VERSION,
  layout: [
    // Scorecard leads — the cross-site comparison table is the primary "where
    // do I act" surface. Then health, then attention, then overview.
    { i: 'org_site_scorecard', x: 0, y: 0, w: ORG_DASHBOARD_COLS, h: 5 },
    { i: 'org_fleet_health_by_site', x: 0, y: 5, w: ORG_MID_WIDTH, h: 5 },
    { i: 'org_device_reachability_by_site', x: ORG_MID_WIDTH, y: 5, w: ORG_MID_WIDTH, h: 5 },
    { i: 'org_unread_unanalysed_by_site', x: ORG_MID_WIDTH * 2, y: 5, w: ORG_MID_WIDTH, h: 5 },
    { i: 'org_total_defects_by_site', x: 0, y: 10, w: ORG_MID_WIDTH, h: 5 },
    { i: 'org_asset_instrumentation_tasks_by_site', x: ORG_MID_WIDTH, y: 10, w: ORG_MID_WIDTH, h: 5 },
    { i: 'org_cost_saved_by_site', x: ORG_MID_WIDTH * 2, y: 10, w: ORG_MID_WIDTH, h: 5 },
    { i: 'org_sites_by_site', x: 0, y: 15, w: ORG_MID_WIDTH, h: 5 }
  ],
  account_dashboard: false
};

/**
 * Merge a saved layout with the defaults.
 * - null/undefined (never saved) → the full default set.
 * - `[]` is a real choice (every widget removed) → stays empty.
 * - Otherwise kept as-is; only each default's minimum size is re-enforced.
 */
export const mergeOrgDashboardLayout = (savedLayout) => {
  if (savedLayout == null) {
    return DEFAULT_ORG_DASHBOARD.layout.map(item => ({ ...item }));
  }
  if (!savedLayout.length) {
    return [];
  }

  const defaultByKey = {};
  DEFAULT_ORG_DASHBOARD.layout.forEach((item) => {
    defaultByKey[item.i] = item;
  });

  return savedLayout.map((item) => {
    const key = item.i.split('#')[0];
    const def = defaultByKey[key];
    if (!def) return { ...item };
    return {
      ...item,
      w: Math.max(item.w, def.w),
      h: Math.max(item.h, def.h)
    };
  });
};

export const buildOrgDashboardFromApi = (apiData, orgId, orgName, t) => {
  const base = apiData || DEFAULT_ORG_DASHBOARD;
  const displayName = orgName
    ? t('orgView.dashboard.titleWithOrg', {
      orgName,
      defaultValue: 'Organisation Dashboard - {{orgName}}'
    })
    : t('orgView.dashboard.title', 'Organisation Dashboard');

  return {
    ...base,
    layout: mergeOrgDashboardLayout(base.layout),
    name: displayName,
    organisation_id: orgId,
    account_dashboard: false
  };
};
